// components/models/LumaRayFlashControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import { LumaRayFlashParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface LumaRayFlashControlPanelProps {
  params: LumaRayFlashParams;
  setModelParams: (params: (prevParams: { [model: string]: LumaRayFlashParams }) => { [model: string]: LumaRayFlashParams }) => void;
  diffusionModel: string;
}

const defaultLumaRayFlashParams: LumaRayFlashParams = {
  prompt: '',
  duration: 5,
  resolution: '540p',
  aspect_ratio: '16:9',
  loop: false,
  seed: -1,
};

export const getDefaultLumaRayFlashParams = (): LumaRayFlashParams => ({ ...defaultLumaRayFlashParams });

// The endpoint takes a "5s" | "9s" enum, converted from this number in app/api/film/route.ts.
const DURATION_OPTIONS = [
  { value: '5', label: '5s' },
  { value: '9', label: '9s' },
];
const RESOLUTION_OPTIONS: NonNullable<LumaRayFlashParams['resolution']>[] = ['540p', '720p', '1080p'];
const ASPECT_RATIO_OPTIONS: NonNullable<LumaRayFlashParams['aspect_ratio']>[] = ['16:9', '9:16', '4:3', '3:4', '21:9', '9:21'];

const LumaRayFlashControlPanel: React.FC<LumaRayFlashControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[LumaRayFlashControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' ? { value_length: (newValue as string).length } : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'LumaRayFlashControlPanel',
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[LumaRayFlashControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: name === 'duration' ? Number(value) : value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'LumaRayFlashControlPanel',
    });
  };

  const onSwitchLoop = () => {
    const newValue = !params.loop;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[LumaRayFlashControlPanel] onSwitchLoop: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, loop: newValue },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'loop',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'LumaRayFlashControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <Select
          id="luma-ray-2-flash-duration"
          label="Duration"
          value={String(params.duration)}
          options={DURATION_OPTIONS}
          onChange={(value) => handleSelectChange('duration', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="luma-ray-2-flash-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={(value) => handleSelectChange('resolution', value)}
        />
      </div>

      <div className="mb-4">
        <Select
          id="luma-ray-2-flash-aspect-ratio"
          label="Aspect Ratio"
          value={params.aspect_ratio ?? '16:9'}
          options={ASPECT_RATIO_OPTIONS}
          onChange={(value) => handleSelectChange('aspect_ratio', value)}
        />
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.loop === true}
          onChange={onSwitchLoop}
          labelRight="Loop"
        />
      </div>
    </>
  );
});

LumaRayFlashControlPanel.displayName = 'LumaRayFlashControlPanel';

export default LumaRayFlashControlPanel;
